/**
 * Google Drive API Service
 *
 * Functions for interacting with the Google Drive API after OAuth authentication
 */

import type { GoogleDriveFile, GoogleDriveFolder, GoogleDriveTokens } from './types';
import { getGoogleTokens, isTokenValid, getValidTokens } from './auth';
import { reportError } from '@/lib/errors';

// Base URL for Google Drive API v3
const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';

/**
 * Make an authenticated request to the Drive API
 *
 * @param endpoint - API endpoint (e.g., '/files')
 * @param tokens - OAuth tokens
 * @param options - Fetch options
 * @returns Parsed JSON response
 */
async function driveRequest<T>(
  endpoint: string,
  tokens: GoogleDriveTokens,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${DRIVE_API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${tokens.accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Drive API error: ${response.status} - ${error}`);
  }

  return response.json();
}

/**
 * List folders in a Google Drive directory
 *
 * Used for folder picker navigation
 *
 * @param parentId - Parent folder ID (default: 'root' for My Drive)
 * @returns Array of folders in the specified directory
 */
export async function listFolders(
  parentId: string = 'root'
): Promise<GoogleDriveFolder[]> {
  const tokens = await getValidTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Google Drive');
  }

  const query = encodeURIComponent(
    `mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`
  );

  const response = await driveRequest<{
    files: Array<{ id: string; name: string }>;
  }>(
    `/files?q=${query}&fields=files(id,name)&orderBy=name`,
    tokens
  );

  return response.files.map((f) => ({
    id: f.id,
    name: f.name,
  }));
}

/**
 * List files in a Google Drive folder
 *
 * Returns both files and folders with metadata for display
 *
 * @param folderId - Folder ID to list (default: 'root' for My Drive)
 * @param pageToken - Token for pagination
 * @returns Files array and optional next page token
 */
export async function listFiles(
  folderId: string = 'root',
  pageToken?: string
): Promise<{ files: GoogleDriveFile[]; nextPageToken?: string }> {
  const tokens = await getValidTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Google Drive');
  }

  const query = encodeURIComponent(
    `'${folderId}' in parents and trashed=false`
  );

  let url = `/files?q=${query}&fields=files(id,name,mimeType,size,modifiedTime,webViewLink,thumbnailLink,parents)&orderBy=folder,name&pageSize=50`;
  if (pageToken) {
    url += `&pageToken=${pageToken}`;
  }

  const response = await driveRequest<{
    files: Array<{
      id: string;
      name: string;
      mimeType: string;
      size?: string;
      modifiedTime?: string;
      webViewLink?: string;
      thumbnailLink?: string;
      parents?: string[];
    }>;
    nextPageToken?: string;
  }>(url, tokens);

  return {
    files: response.files.map((f) => ({
      id: f.id,
      name: f.name,
      mimeType: f.mimeType,
      size: f.size,
      modifiedTime: f.modifiedTime,
      webViewLink: f.webViewLink,
      thumbnailLink: f.thumbnailLink,
      parents: f.parents,
    })),
    nextPageToken: response.nextPageToken,
  };
}

/**
 * Get folder information for breadcrumb navigation
 *
 * @param folderId - Folder ID to get info for
 * @returns Folder info or null if not found
 */
export async function getFolderInfo(
  folderId: string
): Promise<GoogleDriveFolder | null> {
  const tokens = await getValidTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Google Drive');
  }

  // Handle root folder specially
  if (folderId === 'root') {
    return { id: 'root', name: 'My Drive' };
  }

  try {
    const response = await driveRequest<{
      id: string;
      name: string;
      parents?: string[];
    }>(`/files/${folderId}?fields=id,name,parents`, tokens);

    return {
      id: response.id,
      name: response.name,
      path: response.parents?.[0],
    };
  } catch {
    return null;
  }
}

/**
 * Get authenticated user's Google account info
 *
 * Used to display connected account in the UI
 *
 * @returns User info or null if not authenticated
 */
export async function getDriveUserInfo(): Promise<{
  email: string;
  name: string;
  picture?: string;
} | null> {
  const tokens = await getValidTokens();
  if (!tokens) {
    return null;
  }

  try {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v2/userinfo',
      {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      }
    );

    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

/**
 * Check if the user is connected to Google Drive
 *
 * Verifies tokens are valid and can make API requests
 *
 * @returns true if connected and authenticated
 */
export async function checkDriveConnection(): Promise<boolean> {
  const tokens = await getValidTokens();
  if (!tokens) {
    return false;
  }

  try {
    // Simple API call to verify token works
    await driveRequest<{ user: unknown }>('/about?fields=user', tokens);
    return true;
  } catch {
    return false;
  }
}

/**
 * Download file content as ArrayBuffer
 *
 * Used for indexing data room documents for RAG retrieval
 *
 * @param fileId - Google Drive file ID
 * @returns File content as ArrayBuffer, or null if download failed
 */
export async function downloadFileContent(
  fileId: string
): Promise<ArrayBuffer | null> {
  const tokens = await getValidTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Google Drive');
  }

  try {
    // Use alt=media to get the file content instead of metadata
    const response = await fetch(
      `${DRIVE_API_BASE}/files/${fileId}?alt=media`,
      {
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      reportError(new Error(`Drive download error: ${response.status} ${error}`), { context: 'GoogleDriveApi.downloadFile', level: 'warning' });
      return null;
    }

    return response.arrayBuffer();
  } catch (error) {
    reportError(error, { context: 'GoogleDriveApi.downloadFile', level: 'warning' });
    return null;
  }
}

/**
 * Download file content as base64 string
 *
 * Convenience wrapper for indexing documents
 *
 * @param fileId - Google Drive file ID
 * @returns Base64 encoded file content, or null if download failed
 */
export async function downloadFileAsBase64(
  fileId: string
): Promise<string | null> {
  const content = await downloadFileContent(fileId);
  if (!content) return null;

  // Convert ArrayBuffer to base64
  const bytes = new Uint8Array(content);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
